/**
 * userNotifications/{unt_*} — durable per-user inbox (Phase 4.1).
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase/config";
import { NOTIFICATION_INBOX_PAGE_SIZE } from "../domain/notifications/notificationTypes";

const COLLECTION = "userNotifications";

function col() {
  return collection(db, COLLECTION);
}

function ref(id) {
  return doc(db, COLLECTION, id);
}

function fromSnap(snap) {
  if (!snap?.exists()) return null;
  return { ...snap.data(), notificationId: snap.id };
}

/**
 * @param {object} shell
 */
export async function createUserNotification(shell) {
  const id = shell.notificationId;
  if (!id || !shell.userId) throw new Error("notificationId and userId required.");
  const payload = {
    ...shell,
    updatedAtMs: Date.now(),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };
  await setDoc(ref(id), payload);
  return payload;
}

/**
 * @param {string} notificationId
 * @param {object} patch
 */
export async function updateUserNotification(notificationId, patch) {
  await updateDoc(ref(notificationId), {
    ...patch,
    updatedAtMs: Date.now(),
    updatedAt: serverTimestamp(),
  });
}

/**
 * @param {string} notificationId
 */
export async function fetchUserNotification(notificationId) {
  return fromSnap(await getDoc(ref(notificationId)));
}

/**
 * @param {string} userId
 * @param {string} dedupeKey
 */
export async function findByDedupeKey(userId, dedupeKey) {
  if (!userId || !dedupeKey) return null;
  const q = query(
    col(),
    where("userId", "==", userId),
    where("dedupeKey", "==", dedupeKey),
    limit(1)
  );
  const snap = await getDocs(q);
  if (snap.empty) return null;
  return fromSnap(snap.docs[0]);
}

/**
 * Active group row for collapse window (unread/read, not dismissed).
 * @param {string} userId
 * @param {string} groupKey
 * @param {number} sinceMs
 */
export async function findActiveGroupNotification(userId, groupKey, sinceMs) {
  if (!userId || !groupKey) return null;
  const q = query(
    col(),
    where("userId", "==", userId),
    where("groupKey", "==", groupKey),
    orderBy("createdAtMs", "desc"),
    limit(5)
  );
  const snap = await getDocs(q);
  for (const d of snap.docs) {
    const row = fromSnap(d);
    if (!row) continue;
    if (row.status === "dismissed" || row.status === "archived") continue;
    if ((row.createdAtMs || 0) < sinceMs) continue;
    return row;
  }
  return null;
}

/**
 * @param {string} userId
 * @param {{ max?: number }} [opts]
 */
export async function listUserNotifications(userId, { max = NOTIFICATION_INBOX_PAGE_SIZE } = {}) {
  if (!userId) return [];
  const q = query(
    col(),
    where("userId", "==", userId),
    orderBy("createdAtMs", "desc"),
    limit(Math.min(100, max))
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => fromSnap(d)).filter(Boolean);
}

/**
 * @param {string} userId
 * @param {(rows: object[]) => void} onChange
 * @param {{ max?: number }} [opts]
 * @returns {() => void}
 */
export function subscribeUserNotifications(userId, onChange, { max = NOTIFICATION_INBOX_PAGE_SIZE } = {}) {
  if (!userId) {
    onChange([]);
    return () => {};
  }
  const q = query(
    col(),
    where("userId", "==", userId),
    orderBy("createdAtMs", "desc"),
    limit(Math.min(100, max))
  );
  return onSnapshot(
    q,
    (snap) => {
      onChange(snap.docs.map((d) => fromSnap(d)).filter(Boolean));
    },
    () => {
      onChange([]);
    }
  );
}
